import React, { ChangeEvent } from 'react';
import styled, { css } from 'styled-components';
import FlexboxContainer from 'components/UI/FlexboxContainer';
import { Height } from 'styles/themeHeights';
import { Background, Border, Main, Text } from 'styles/themeColors';
import { Space } from 'styles/themeSpaces';
import { BorderRadius } from 'styles/themeBorderRadius';
import { LabelPosition } from 'types/position';
import { ReadOnlyWrapper } from 'components/common/ReadOnlyWrapper';

export type RadioVariant = 'default' | 'tile';

const Input = styled.input`
  border: 4px solid white;
  border-radius: ${BorderRadius.l};
  width: ${Height.s};
  height: ${Height.s};
  background-color: ${Background.white};

  &:checked {
    background-color: ${Main.brandLight};
  }

  &:disabled {
    background-color: ${Main.disabled};
    border: 2px solid ${Border.main};
  }
`;

// Custom-drawn radio circle used inside the tile variant so the checked
// state matches the design (orange ring + solid orange dot).
const TileInput = styled.input<{ $color?: string }>`
  appearance: none;
  -webkit-appearance: none;
  flex-shrink: 0;
  margin: 0;
  width: ${Height.s};
  height: ${Height.s};
  border-radius: 50%;
  border: 2px solid ${Border.main};
  background-color: ${Background.white};
  cursor: pointer;
  display: inline-grid;
  place-content: center;
  transition: border-color 0.15s ease;

  &::before {
    content: '';
    width: 55%;
    height: 55%;
    border-radius: 50%;
    transform: scale(0);
    transition: transform 0.1s ease-in-out;
    background-color: ${({ $color }) => $color || Main.brandLight};
  }

  &:checked {
    border: 7px solid ${({ $color }) => $color || Main.brandLight};
  }

  &:checked::before {
    transform: scale(1);
  }

  &:disabled {
    cursor: not-allowed;
    background-color: ${Main.disabled};
    border-color: ${Border.main};

    &::before {
      background-color: ${Border.main};
    }
  }
`;

const Tile = styled.label<{ $checked: boolean; $disabled: boolean; $color?: string; $compact?: boolean }>`
  display: flex;
  align-items: center;
  gap: ${Space.spHalfPlus};
  width: 100%;
  padding: ${({ $compact }) => ($compact ? Space.spBase : Space.spBasePlus)} ${Space.sp2x};
  border-radius: ${BorderRadius.m};
  border: 1px solid ${Border.main};
  background-color: ${Background.white};
  cursor: pointer;
  user-select: none;
  transition: border-color 0.15s ease, background-color 0.15s ease;

  ${({ $checked, $color }) =>
    $checked &&
    css`
      border-color: ${$color || Main.brandLight};
      background-color: ${'#FFF8F2'};
    `}

  ${({ $disabled }) =>
    $disabled &&
    css`
      cursor: not-allowed;
      opacity: 0.6;
    `}

  &:hover {
    ${({ $checked, $disabled, $color }) =>
      !$checked && !$disabled && css`border-color: ${$color || Main.brandLight};`}
  }

  &:focus-within {
    outline: 2px solid ${({ $color }) => $color || Main.brandLight};
    outline-offset: 1px;
  }
`;

const TileLabelText = styled.span`
  color: ${Text.primary};
`;

const Label = styled.label<any>`
  text-align: center;
`;

interface Props {
  id: string;
  className?: string;
  name: string;
  value?: string;
  checked?: boolean;
  disabled?: boolean;
  label?: string;
  labelPosition?: LabelPosition;
  color?: string;
  variant?: RadioVariant;
  onChange?: (checked: boolean) => void;
  onChangeValue?: (value: string) => void;
  readOnly?: boolean;
  compact?: boolean;
}

const RadioButton = ({
  id,
  className = '',
  name,
  value,
  checked = false,
  disabled = false,
  label,
  labelPosition = 'right',
  color,
  variant = 'default',
  onChange,
  onChangeValue,
  readOnly,
  compact = false
}: Props) => {
  const handleChange = (event: ChangeEvent<HTMLInputElement>) => {
    if (onChange) {
      onChange(event.target.checked);
    } else if (onChangeValue) {
      onChangeValue(event.target.value);
    }
  };

  if (variant === 'tile') {
    return (
      <Tile
        htmlFor={id}
        className={className}
        $checked={checked}
        $disabled={readOnly || disabled}
        $color={color}
        $compact={compact}
      >
        <ReadOnlyWrapper readOnly={readOnly}>
          <TileInput
            id={id}
            name={name}
            value={value}
            checked={checked}
            disabled={readOnly || disabled}
            onChange={handleChange}
            type="radio"
            $color={color}
          />
        </ReadOnlyWrapper>
        {label && <TileLabelText className="base">{label}</TileLabelText>}
      </Tile>
    );
  }

  // ----- legacy variant (unchanged) -----
  const labelStyle = labelPosition === 'left'
    ? { marginRight: `${Space.spHalfPlus}` }
    : { marginLeft: `${Space.spHalfPlus}` };

  const labelElement = <Label htmlFor={id} style={labelStyle} className="base">{label}</Label>;
  const getStyles = styles({ color, disabled });

  return (
    <FlexboxContainer
      alignItems="center"
      padding={label ? `${Space.spHalfPlus} ${Space.sp2x} ${Space.spHalfPlus} 0` : '0'}
      displayFlex="inline-flex"
    >
      {label && labelPosition === 'left' ? labelElement : null}
      <ReadOnlyWrapper readOnly={readOnly}>
        <Input
          id={id}
          style={getStyles.radioButton as any}
          className={className}
          name={name}
          value={value}
          checked={checked}
          disabled={readOnly || disabled}
          onChange={handleChange}
          type="radio"
        />
      </ReadOnlyWrapper>
      {label && labelPosition === 'right' ? labelElement : null}
    </FlexboxContainer>
  );
};

const styles = (props: any) => ({
  radioButton: {
    appearance: props.color && !props.disabled ? 'none' : 'radio',
    boxShadow: props.color && !props.disabled ? `0 0 0 1px ${props.color}` : 'none',
    cursor: 'pointer'
  },
});

export default RadioButton;